import { getApp } from 'firebase/app'
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
  type UserCredential,
} from 'firebase/auth'
import { User as FitifyUser } from 'fitify-types/src/types/user'
import { useRouter } from 'next/router'
import {
  type Context,
  type FC,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { showToast } from '../utils/toast'

interface AuthContextType {
  user: User | null
  isAuthLoading: boolean
  isLoggedIn: boolean
  logout: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
}

const AuthContext: Context<AuthContextType> = createContext<AuthContextType>({
  user: null,
  isAuthLoading: true,
  isLoggedIn: false,
  logout: async () => Promise.resolve(undefined),
  signIn: async () => Promise.resolve(undefined),
})

export const AuthContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [user, setUser] = useState<User | null>(null)

  const router = useRouter()

  const cleanContext = useCallback(() => {
    setUser(null)
    setIsAuthLoading(false)
    setIsLoggedIn(false)
  }, [])

  const signIn = useCallback(
    async (email: string, password: string) => {
      setIsAuthLoading(true)
      try {
        const response: UserCredential = await signInWithEmailAndPassword(
          getAuth(),
          email,
          password
        )

        const userClaims = await response.user.getIdTokenResult()
        if (
          userClaims.claims.role === FitifyUser.AuthData.CustomClaims.Role.Admin
        ) {
          setUser(response.user)
          setIsLoggedIn(true)
          setIsAuthLoading(false)
          await router.push('/campaigns')
        } else {
          showToast("You don't have permission to access this page.", {
            type: 'error',
          })
          await signOut(getAuth())
          cleanContext()
        }
      } catch {
        showToast('Invalid email of password. Please try again.', {
          type: 'error',
        })
      } finally {
        setIsAuthLoading(false)
      }
    },
    [cleanContext, router]
  )

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const app = getApp()

    return onAuthStateChanged(getAuth(app), async (authUser) => {
      if (authUser) {
        setUser(authUser)
        setIsLoggedIn(true)
        setIsAuthLoading(false)
      } else {
        cleanContext()
        await router.replace('/')
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  const value = useMemo(
    () => ({
      user,
      isAuthLoading,
      isLoggedIn,
      logout: async () => {
        await signOut(getAuth())
        cleanContext()
      },
      signIn,
    }),
    [user, isAuthLoading, isLoggedIn, signIn, cleanContext]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthContextProvider')
  }
  return context
}
